import { useCallback, useEffect, useState } from 'react';
import {
  type AdController,
  type ShowPromiseResult,
} from '@common/types/adsgram';

import { ENV } from '@/common/app/env';
import {
  type AdsCallbackArgsType,
  type AdsPlacementsType,
  type UseAdsHookReturnType,
} from '@/common/types/ads';

const adsgramIdByPlacements: Record<AdsPlacementsType, string> = {
  resource: ENV.RESOURCE_BOOST_BLOCK_ID,
  material: ENV.MATERIAL_BOOST_BLOCK_ID,
  goods: ENV.GOODS_BOOST_BLOCK_ID,
  daily: ENV.ADSGRAM_DAILY_BONUS_BLOCK_ID,
  marketBox: ENV.ADSGRAM_BOX_MARKET_BLOCK_ID,
  welcomeback: ENV.WELCOMEBACK_BLOCK_ID,
  chest: ENV.CHESTBOX_BLOCK_ID,
  scratch: ENV.CHESTBOX_BLOCK_ID,
};

// show() resolves or rejects with a ShowPromiseResult; keep its description
// and state so ad_provider_failed tells why Adsgram refused
const toAdsgramError = (reason: unknown): Error => {
  if (reason instanceof Error) {
    return reason;
  }

  if (reason && typeof reason === 'object' && 'description' in reason) {
    const { description, state } = reason as ShowPromiseResult;

    return new Error(`Adsgram: ${description} (state: ${state})`);
  }

  return new Error(`Adsgram: ${String(reason)}`);
};

const useAdsgramAds = (): UseAdsHookReturnType => {
  const [AdController, setAdController] = useState<AdController | undefined>();
  const runAdsgramAds = useCallback(
    async ({ placements, onReward }: AdsCallbackArgsType): Promise<void> => {
      try {
        console.log('run adsgram ads');
        const blockId = adsgramIdByPlacements[placements];

        if (!blockId || isNaN(Number(blockId))) {
          throw new Error(
            'Adsgram block id for this placement is missing, or not valid',
          );
        }

        const AdController = window.Adsgram?.init({
          blockId,
          debug: false,
          debugBannerType: 'RewardedVideo',
        });
        setAdController(AdController);

        if (AdController) {
          const result = await AdController.show();

          if (onReward && result.done && !result.error) {
            onReward();
          }

          if (result.error) {
            throw toAdsgramError(result);
          }
        } else {
          throw new Error('Adsgram script not initialized');
        }
      } catch (error) {
        console.error(error);
        throw toAdsgramError(error);
      }
    },
    [],
  );

  useEffect(() => {
    const onTooLongSession = (): void => {
      console.log('Adsgram: Too long sesion');
    };

    const onBannerNotFound = (): void => {
      console.log('Adsgram: is no banner to display');
    };

    const onNonStopShow = (): void => {
      console.log('Adsgram: trying to watch several ads in a row');
    };

    const onError = (): void => {
      console.log('Adsgram: error');
    };

    AdController?.addEventListener('onTooLongSession', onTooLongSession);
    AdController?.addEventListener('onBannerNotFound', onBannerNotFound);
    AdController?.addEventListener('onNonStopShow', onNonStopShow);
    AdController?.addEventListener('onError', onError);

    return (): void => {
      AdController?.removeEventListener('onTooLongSession', onTooLongSession);
      AdController?.removeEventListener('onBannerNotFound', onBannerNotFound);
      AdController?.removeEventListener('onNonStopShow', onNonStopShow);
      AdController?.removeEventListener('onError', onError);
    };
  }, [AdController]);

  return runAdsgramAds;
};

export { useAdsgramAds };
